import React, { useState } from 'react';
import { Mail, MapPin, Clock, Users, MessageSquare, Lightbulb, Briefcase, Send, CheckCircle, Globe, Github, Linkedin, Twitter } from 'lucide-react';
// import contactHero from '../assets/images/contact-hero.jpg';

const ContactCard = ({ icon: Icon, title, description, email, delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="bg-white rounded-xl shadow-lg p-6 border-2 border-transparent hover:border-blue-500 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
      style={{ animationDelay: `${delay}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 ${
        isHovered ? 'bg-blue-600 scale-110' : 'bg-blue-100'
      }`}>
        <Icon className={`w-7 h-7 transition-colors duration-300 ${
          isHovered ? 'text-white' : 'text-blue-600'
        }`} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-4 leading-relaxed">{description}</p>
      <a 
        href={`mailto:${email}`}
        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-semibold group"
      >
        <Mail className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        {email}
      </a>
    </div>
  );
};

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="bg-gradient-to-br from-white to-blue-50 rounded-xl p-6 border border-blue-100 shadow-md hover:shadow-lg transition-all duration-300">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
    </div>
    <div className="text-gray-700 space-y-2">
      {children}
    </div>
  </div>
);

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', subject: 'general', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <Send className="w-6 h-6 text-blue-600" />
        <h2 className="text-2xl font-bold text-gray-900">Send us a Message</h2>
      </div>

      {isSubmitted ? (
        <div className="py-12 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4 animate-bounce" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
          <p className="text-gray-600">We'll get back to you within 24-48 hours.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
                placeholder="john@example.com"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Subject
            </label>
            <select
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none"
            >
              <option value="general">General Inquiry</option>
              <option value="support">Product Support</option>
              <option value="collaboration">Collaboration</option>
              <option value="feedback">Feedback</option>
              <option value="recruiting">Recruiting</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={6}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all outline-none resize-none"
              placeholder="Tell us how we can help..."
              required
            />
          </div>

          <button
            onClick={handleSubmit}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 hover:shadow-xl flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Send Message
          </button>
        </div>
      )}
    </div>
  );
};

export default function GodlikeContactPage() {
  const contactOptions = [
    {
      icon: MessageSquare,
      title: 'General Inquiries',
      description: 'Questions about GnosisFT, product recommendations, models, or data sources?',
      email: 'support@gnosisft.com',
      delay: 0
    },
    {
      icon: Lightbulb,
      title: 'Product & Feedback',
      description: 'Found a bug, have feedback, or want to suggest a feature?',
      email: 'support@gnosisft.com',
      delay: 100
    },
    {
      icon: Users,
      title: 'Collaboration & Research',
      description: 'Interested in collaboration, research discussions, or contributing ideas?',
      email: 'info@gnosisft.com',
      delay: 200
    },
    {
      icon: Briefcase,
      title: 'Recruiting / Contribution',
      description: 'We welcome voluntary collaboration and research contributions. Currently no paid roles.',
      email: 'info@gnosisft.com',
      delay: 300
    }
  ];

  return (
    <div className="min-h-screen from-slate-50 via-blue-50 to-indigo-50 overflow-y-auto pb-16">
      {/* Hero Section */}
      <div className="relative text-white overflow-hidden bg-[url('../assets/images/contact-hero.jpg')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-50"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-blue-500 rounded-full opacity-10 -top-48 -left-48 blur-3xl"></div>
          <div className="absolute w-96 h-96 bg-purple-500 rounded-full opacity-10 -bottom-48 -right-48 blur-3xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-24 text-center">
          <h1 className="text-6xl font-bold mb-6 animate-fade-in">
            Get in Touch
          </h1>
          <p className="text-2xl text-blue-200 mb-8 max-w-2xl mx-auto">
            We'd love to hear from you. Let's start a conversation.
          </p>
          <div className="flex justify-center gap-4">
            <a href="#contact-form" className="bg-white text-blue-900 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-all transform hover:scale-105 shadow-lg">
              Send Message
            </a>
            <a href="mailto:info@gnosisft.com" className="border-2 border-white text-white px-8 py-3 rounded-lg font-bold hover:bg-white hover:text-blue-900 transition-all">
              Quick Email
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Contact Options Grid */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How Can We Help?</h2>
            <p className="text-xl text-gray-600">Choose the best way to reach out</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contactOptions.map((option, index) => (
              <ContactCard key={index} {...option} />
            ))}
          </div>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Left: Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            <InfoCard icon={MapPin} title="Location">
              <p className="font-semibold">California, United States</p>
              <p className="text-sm text-gray-600">Remote / Work-from-home</p>
            </InfoCard>

            <InfoCard icon={Clock} title="Response Time">
              <p>We usually respond within <span className="font-semibold text-blue-600">24–48 hours</span></p>
              <p className="text-sm text-gray-600">Weekdays: Priority response</p>
            </InfoCard>

            <InfoCard icon={Globe} title="About GnosisFT">
              <p className="text-sm leading-relaxed">
                GnosisFT is an independent AI-powered product recommendation platform focused on transparency, 
                research-driven insights, and practical decision-making.
              </p>
            </InfoCard>

            {/* Social Links */}
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-md">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Connect With Us</h3>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center text-white hover:bg-blue-700 transition-all transform hover:scale-110">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center text-white hover:bg-gray-800 transition-all transform hover:scale-110">
                  <Github className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 bg-blue-700 rounded-lg flex items-center justify-center text-white hover:bg-blue-800 transition-all transform hover:scale-110">
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-2" id="contact-form">
            <ContactForm />
          </div>
        </div>

        {/* Footer Note */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 text-center">
          <p className="text-gray-700">
            🔒 By contacting us, you agree that we may respond via email. 
            <span className="font-semibold text-blue-900"> We do not share or sell contact information.</span>
          </p>
        </div>
      </div>

      {/* Floating Action Button */}
      <a
        href="mailto:info@gnosisft.com"
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform z-50 group"
      >
        <Mail className="w-7 h-7 group-hover:rotate-12 transition-transform" />
      </a>
    </div>
  );
}